import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";

// LCP (Largest Contentful Paint) asset: pre-downloaded and converted to local, optimized
// WebP files (was a remote Pexels URL - every extra DNS lookup/TLS handshake/CDN hop
// before the very first byte directly delays LCP). srcSet + sizes="100vw" let the
// browser pick the smallest size that still covers the viewport instead of always
// downloading the full 1920px asset on a 390px phone.
const HERO_SRCSET = [
  `${process.env.PUBLIC_URL}/assets/hero-640.webp 640w`,
  `${process.env.PUBLIC_URL}/assets/hero-1024.webp 1024w`,
  `${process.env.PUBLIC_URL}/assets/hero-1536.webp 1536w`,
  `${process.env.PUBLIC_URL}/assets/hero-1920.webp 1920w`,
].join(", ");
const HERO_FALLBACK_SRC = `${process.env.PUBLIC_URL}/assets/hero-1920.webp`;

const HeroSection = ({ onOpenInquiry }) => (
  <section
    className="relative overflow-hidden min-h-[max(560px,calc(100svh_-_72px))] md:min-h-[max(680px,calc(100svh_-_80px))] flex items-center"
    data-testid="hero-section"
  >
    <div className="absolute inset-0">
      {/*
        This is the page's LCP element - it must never be lazy-loaded or left at
        default/auto priority: loading="eager" + fetchPriority="high" tell the browser
        to discover and fetch it immediately (matched by an equivalent <link rel=preload
        imagesrcset=...> in public/index.html so it starts downloading before React even
        hydrates), instead of competing with/losing to the hero text and other requests.
      */}
      <img
        src={HERO_FALLBACK_SRC}
        srcSet={HERO_SRCSET}
        sizes="100vw"
        alt="Abdullah Karslı Premium Mülk"
        className="w-full h-full object-cover"
        loading="eager"
        fetchPriority="high"
        decoding="async"
      />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(43,43,43,0.62)_0%,rgba(43,43,43,0.42)_45%,rgba(43,43,43,0.88)_100%)]" />
    </div>

    <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 w-full">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col gap-6 max-w-2xl"
      >
        <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] text-white bg-white/10 backdrop-blur-sm border border-white/25 rounded-full px-4 py-2 w-fit">
          Abdullah Karslı Gayrimenkul Danışmanlığı
        </span>
        <h1 className="font-[var(--font-serif)] text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] text-white leading-[1.08]">
          Aradığınız Mülk, <span className="gold-gradient-text">Hayal Ettiğiniz</span> Yaşam
        </h1>
        <p className="text-base md:text-lg text-white/85 max-w-lg leading-relaxed">
          Abdullah Karslı Gayrimenkul Danışmanlığı, prestijli daireler, yatırımlık arsalar, verimli tarlalar ve huzurlu
          bahçeler arasından sizin için en değerli olanı bulur.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            data-testid="hero-cta-request"
            onClick={onOpenInquiry}
            className="h-12 px-6 rounded-[12px] bg-[linear-gradient(135deg,#D4AF37_0%,#C5A059_55%,#E7D7A6_100%)] text-[color:var(--brand-charcoal)] font-medium shadow-[0_10px_30px_rgba(0,0,0,0.25)] hover:shadow-[0_16px_44px_rgba(0,0,0,0.32)] hover:-translate-y-[1px] transition-[box-shadow,transform] duration-200"
          >
            Talep Oluştur
          </Button>
          <Button
            asChild
            variant="outline"
            data-testid="hero-cta-browse"
            className="h-12 px-6 rounded-[12px] border-white/40 text-white font-medium bg-white/5 hover:bg-white/15 backdrop-blur-sm"
          >
            <a href="#kategoriler">İlanları İncele</a>
          </Button>
        </div>
      </motion.div>
    </div>
  </section>
);

export default HeroSection;
